import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import api from '../utils/api';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';

export default function Login({ setIsAuthenticated }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    mobile_number: '',
    password: '',
    full_name: '',
    role: 'member'
  });
  const [loading, setLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetData, setResetData] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [resetLoading, setResetLoading] = useState(false);
  // Forgot password (request-and-approve flow)
  const [showForgotDialog, setShowForgotDialog] = useState(false);
  const [forgotMobile, setForgotMobile] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate mobile number (Indian 10-digit format, starts with 6-9)
    const digits = String(formData.mobile_number || '').replace(/\D/g, '');
    const normalized = digits.startsWith('91') && digits.length === 12
      ? digits.slice(2)
      : digits.startsWith('0') && digits.length === 11 ? digits.slice(1) : digits;
    if (!/^[6-9]\d{9}$/.test(normalized)) {
      toast.error('Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.');
      return;
    }

    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { mobile_number: normalized, password: formData.password }
        : { ...formData, mobile_number: normalized };
      
      const response = await api.post(endpoint, payload);

      if (isLogin && response.data.password_reset_required) {
        // Store token temporarily for the password change call
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setResetData({ old_password: formData.password, new_password: '', confirm_password: '' });
        setShowResetDialog(true);
        toast.warning('Your password has expired. Please set a new password to continue.');
      } else if (isLogin) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Welcome back!');
        setIsAuthenticated(true);
      } else {
        toast.success(response.data.message || 'Account created. Pending approval.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (resetData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (resetData.new_password !== resetData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (resetData.new_password === resetData.old_password) {
      toast.error('New password must be different from the current password');
      return;
    }
    setResetLoading(true);
    try {
      await api.put('/auth/change-password', {
        old_password: resetData.old_password,
        new_password: resetData.new_password
      });
      toast.success('Password updated successfully!');
      setShowResetDialog(false);
      setIsAuthenticated(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetCancel = () => {
    setShowResetDialog(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { mobile_number: forgotMobile });
      // Backend always returns a generic message to prevent user enumeration
      toast.success(res.data?.message || 'If an account exists for this number, your Super Admin has been notified.');
      setShowForgotDialog(false);
      setForgotMobile('');
    } catch (err) {
      // Even on transport error don't leak details
      toast.success('If an account exists for this number, your Super Admin has been notified.');
      setShowForgotDialog(false);
      setForgotMobile('');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1759684137007-32dcf14ded31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdCUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzcyODg2MTY5fDA&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#4338CA]/90 to-[#059669]/70"></div>
        <div className="relative z-10 p-12 text-white flex flex-col justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 bg-white rounded-xl p-3 flex items-center justify-center">
              <img 
                src="/company-logo.jpg" 
                alt="Company Logo" 
                className="h-full w-full object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold">FinTrack Pro</h1>
          </div>
          <div>
            <h2 className="text-4xl font-bold mb-4">Streamline Your Financial Operations</h2>
            <p className="text-xl text-white/90">Manage tasks, track time, and deliver exceptional service to your clients.</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <Card className="w-full max-w-md border-slate-200 shadow-lg" data-testid="auth-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold tracking-tight">
              {isLogin ? 'Welcome back' : 'Create account'}
            </CardTitle>
            <CardDescription className="text-base">
              {isLogin ? 'Enter your credentials to access your account' : 'Fill in your details to get started'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    data-testid="full-name-input"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required={!isLogin}
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="mobile_number">Mobile Number</Label>
                <Input
                  id="mobile_number"
                  data-testid="mobile-number-input"
                  type="tel"
                  placeholder="9876543210"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="role-select">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Team Member</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-11 bg-[#4338CA] hover:bg-[#3730A3]" 
                disabled={loading}
                data-testid="auth-submit-button"
              >
                {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm space-y-2">
              {isLogin && (
                <button
                  type="button"
                  onClick={() => { setForgotMobile(formData.mobile_number || ''); setShowForgotDialog(true); }}
                  className="block w-full text-[#4338CA] hover:text-[#3730A3] font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#4338CA] hover:text-[#3730A3] font-medium"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forced Password Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={(open) => { if (!open) handleResetCancel(); }}>
        <DialogContent className="max-w-md" data-testid="forced-password-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <KeyRound className="h-5 w-5 text-amber-500" /> Password Reset Required
            </DialogTitle>
            <DialogDescription>
              Your password has expired. Please create a new password to continue. Passwords must be changed every 30 days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password <span className="text-red-500">*</span></Label>
              <Input id="new_password" data-testid="new-password-input" type="password" placeholder="Enter new password"
                value={resetData.new_password} onChange={(e) => setResetData({ ...resetData, new_password: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password <span className="text-red-500">*</span></Label>
              <Input id="confirm_password" data-testid="confirm-password-input" type="password" placeholder="Confirm new password"
                value={resetData.confirm_password} onChange={(e) => setResetData({ ...resetData, confirm_password: e.target.value })} required />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#4338CA] hover:bg-[#3730A3]" disabled={resetLoading}
                data-testid="submit-new-password-btn">
                {resetLoading ? 'Updating...' : 'Update Password'}
              </Button>
              <Button type="button" variant="outline" onClick={handleResetCancel} data-testid="cancel-reset-btn">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Request Dialog */}
      <Dialog open={showForgotDialog} onOpenChange={setShowForgotDialog}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <KeyRound className="h-5 w-5 text-amber-500" /> Forgot Password
            </DialogTitle>
            <DialogDescription>
              Enter your registered mobile number. Your Super Admin will review the request and share a temporary password with you.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="forgot_mobile">Mobile Number <span className="text-red-500">*</span></Label>
              <Input
                id="forgot_mobile"
                data-testid="forgot-mobile-input"
                type="tel"
                placeholder="9876543210"
                value={forgotMobile}
                onChange={(e) => setForgotMobile(e.target.value)}
                required
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="flex-1 bg-[#4338CA] hover:bg-[#3730A3]"
                disabled={forgotLoading || !forgotMobile}
                data-testid="submit-forgot-btn"
              >
                {forgotLoading ? 'Submitting...' : 'Request Reset'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForgotDialog(false)} data-testid="cancel-forgot-btn">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}